/**
 * Register.jsx - Scriptus Pharma Partner
 * Página unificada de Landing + Registro focada em conversão
 * Design: Glassmorphism consistente com Scriptus RxIA
 * INTEGRADO com Configuracoes.jsx - dados salvos no formato companyData
 * 
 * ✅ CORRIGIDO: Integração com AuthContext e Backend
 * ✅ CORRIGIDO: Estrutura padronizada PT-BR
 * ✅ CORRIGIDO: Removida duplicação de campos
 * ✅ CORRIGIDO: Códigos de erro alinhados com backend
 * ✅ CORRIGIDO: Verificação de conta alinhada com Login.jsx
 * ✅ CORRIGIDO: Logout antes de exibir mensagem de bloqueio
 * ✅ CORRIGIDO: Uso de deleteField() para limpar campos
 * ✅ CORRIGIDO: Helpers centralizados importados do AuthContext
 * ✅ NOVO: Login automático para email existente
 * ✅ NOVO: Verificação de conta arquivada
 * ✅ NOVO: Delay estratégico antes do Stripe
 * 
 * PLANOS:
 * - Basic: Gratuito (50 dispensações/mês)
 * - Partner: R$100/mês via Stripe (ilimitado + marketplace)
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  Building2, 
  Mail, 
  Lock, 
  Phone, 
  FileCheck,
  ArrowRight,
  Loader2,
  Check,
  Sparkles,
  Shield,
  Zap,
  Clock,
  FileText,
  QrCode,
  Crown,
  TrendingUp,
  Users,
  Infinity
} from 'lucide-react';

// ✅ CORRIGIDO: Importar helpers centralizados do AuthContext
import { 
  useAuth,
  isContaArquivada,
  isContaBloqueada,
  isContaAtiva
} from '../contexts/AuthContext';

// ✅ CORRIGIDO: Imports completos do Firebase
import { auth, db } from '../config/firebase';
import { signInWithEmailAndPassword, signOut as firebaseSignOut } from 'firebase/auth';
import { doc, getDoc, updateDoc, deleteField } from 'firebase/firestore';

// 🔧 Configuração da API
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.scriptusrxia.com.br';

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, loading: authLoading } = useAuth();
  const [scrollY, setScrollY] = useState(0);
  
  const returnUrl = searchParams.get('returnUrl');
  const preSelectedPlan = searchParams.get('plano');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    tradeName: '',
    cnpj: '',
    crf: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  
  const [errors, setErrors] = useState({});
  const [erro, setErro] = useState('');
  const [mensagemTipo, setMensagemTipo] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(preSelectedPlan === 'partner' ? 'partner' : 'basic');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (preSelectedPlan === 'partner') {
      setSelectedPlan('partner');
    }
  }, [preSelectedPlan]);

  // ✅ REMOVIDO: Helpers locais duplicados
  // Agora usando helpers centralizados importados do AuthContext:
  // - isContaArquivada
  // - isContaBloqueada
  // - isContaAtiva

  // ============================================
  // SISTEMA DE MENSAGENS
  // ============================================

  const exibirMensagem = (tipo, mensagem) => {
    setMensagemTipo(tipo);
    setErro(mensagem);
  };

  const limparMensagem = () => {
    setErro('');
    setMensagemTipo('');
  };

  const IconeMensagem = ({ tipo }) => {
    const icones = {
      erro: (
        <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      ),
      aviso: (
        <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      info: (
        <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      bloqueado: (
        <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
      sucesso: (
        <svg className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    };
    return icones[tipo] || icones.erro;
  };

  const getEstiloMensagem = (tipo) => {
    const estilos = {
      erro: 'bg-red-50/80 border-red-200/50 text-red-600',
      aviso: 'bg-amber-50/80 border-amber-200/50 text-amber-700',
      info: 'bg-blue-50/80 border-blue-200/50 text-blue-700',
      bloqueado: 'bg-red-100/80 border-red-300/50 text-red-700',
      sucesso: 'bg-emerald-50/80 border-emerald-200/50 text-emerald-600'
    };
    return estilos[tipo] || estilos.erro;
  };

  // ============================================
  // VALIDAÇÃO E FORMATAÇÃO
  // ============================================

  function formatCNPJ(value) {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .slice(0, 18);
  }

  function formatPhone(value) {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .slice(0, 15);
  }

  function validateForm() {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Razão social é obrigatória';
    }
    
    if (!formData.cnpj || formData.cnpj.replace(/\D/g, '').length !== 14) {
      newErrors.cnpj = 'CNPJ inválido';
    }
    
    if (!formData.crf.trim()) {
      newErrors.crf = 'CRF é obrigatório';
    }
    
    if (!formData.email.includes('@')) {
      newErrors.email = 'Email inválido';
    }
    
    if (formData.password.length < 6) {
      newErrors.password = 'Mínimo 6 caracteres';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Senhas não coincidem';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  // ============================================
  // HANDLERS
  // ============================================

  function handleChange(e) {
    const { name, value } = e.target;
    let formattedValue = value;
    
    if (name === 'cnpj') {
      formattedValue = formatCNPJ(value);
    } else if (name === 'phone') {
      formattedValue = formatPhone(value);
    }
    
    setFormData(prev => ({ ...prev, [name]: formattedValue }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
    
    if (erro) {
      limparMensagem();
    }
  }

  function handlePlanSelect(planId) {
    setSelectedPlan(planId);
    limparMensagem();
  }

  // ============================================
  // ✅ CRIAR CHECKOUT STRIPE PARA PARTNER
  // ============================================
  async function createPartnerCheckout(token) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/payment/pharmacy/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          source: 'register'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || data.error || 'Erro ao criar sessão de pagamento');
      }

      return data;
    } catch (error) {
      console.error('Erro ao criar checkout:', error);
      throw error;
    }
  }

  // ============================================
  // ✅ MAPEAMENTO DE CÓDIGOS DE ERRO (PT-BR)
  // ============================================
  function tratarErroRegistro(errorCode, errorMessage) {
    const errorMap = {
      // Códigos PT-BR do backend
      'auth/email-ja-cadastrado': {
        tipo: 'info',
        mensagem: 'Este email já possui uma conta. Clique em "Fazer login" abaixo para acessar sua conta existente.'
      },
      'auth/cnpj-ja-cadastrado': {
        tipo: 'erro',
        mensagem: 'Este CNPJ já está cadastrado na plataforma. Se você já tem uma conta, faça login.'
      },
      'auth/email-invalido': {
        tipo: 'erro',
        mensagem: 'O formato do email não está correto. Por favor, digite um email válido.'
      },
      'auth/senha-fraca': {
        tipo: 'aviso',
        mensagem: 'A senha escolhida é muito fraca. Use pelo menos 6 caracteres combinando letras e números.'
      },
      'validacao/campos-obrigatorios': {
        tipo: 'aviso',
        mensagem: 'Por favor, preencha todos os campos obrigatórios.'
      },
      'validacao/senha-fraca': {
        tipo: 'aviso',
        mensagem: 'A senha deve ter pelo menos 6 caracteres.'
      },
      'validacao/dados-empresa': {
        tipo: 'aviso',
        mensagem: 'CNPJ e CRF são obrigatórios para o cadastro.'
      },
      'validacao/cnpj-invalido': {
        tipo: 'erro',
        mensagem: 'O CNPJ informado é inválido. Verifique e tente novamente.'
      },
      'servidor/erro-interno': {
        tipo: 'erro',
        mensagem: 'Ocorreu um erro no servidor. Por favor, tente novamente em alguns instantes.'
      },
      'auth/erro-desconhecido': {
        tipo: 'erro',
        mensagem: 'Ocorreu um erro ao criar sua conta. Por favor, tente novamente.'
      },
      
      // Códigos Firebase (EN) - fallback
      'auth/email-already-in-use': {
        tipo: 'info',
        mensagem: 'Este email já possui uma conta. Clique em "Fazer login" abaixo para acessar sua conta existente.'
      },
      'auth/email-already-exists': {
        tipo: 'info',
        mensagem: 'Este email já possui uma conta. Clique em "Fazer login" abaixo para acessar sua conta existente.'
      },
      'auth/weak-password': {
        tipo: 'aviso',
        mensagem: 'A senha escolhida é muito fraca. Use pelo menos 6 caracteres combinando letras e números.'
      },
      'auth/invalid-email': {
        tipo: 'erro',
        mensagem: 'O formato do email não está correto. Por favor, digite um email válido.'
      },
      'auth/network-request-failed': {
        tipo: 'aviso',
        mensagem: 'Não conseguimos conectar ao servidor. Verifique sua conexão com a internet.'
      },
      'auth/too-many-requests': {
        tipo: 'bloqueado',
        mensagem: 'Muitas tentativas seguidas. Por segurança, aguarde alguns minutos antes de tentar novamente.'
      },
      'auth/wrong-password': {
        tipo: 'erro',
        mensagem: 'Este email já está cadastrado, mas a senha está incorreta. Se você já tem uma conta, clique em "Fazer login" e use sua senha existente. Se esqueceu sua senha, você pode recuperá-la na página de login.'
      },
      'auth/invalid-credential': {
        tipo: 'erro',
        mensagem: 'Este email já está cadastrado, mas a senha está incorreta. Clique em "Fazer login" abaixo para acessar sua conta ou recuperar sua senha.'
      }
    };

    const errorInfo = errorMap[errorCode];
    
    if (errorInfo) {
      return errorInfo;
    }

    return {
      tipo: 'erro',
      mensagem: errorMessage || 'Ocorreu um erro ao criar sua conta. Por favor, tente novamente.'
    };
  }

  // ============================================
  // ✅ CORRIGIDO: Verificar estado da conta existente
  // Usando helpers centralizados do AuthContext
  // ============================================
  async function verificarEstadoConta(uid) {
    try {
      const docRef = doc(db, "pharmacies", uid);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return { exists: false };
      }
      
      const userData = docSnap.data();
      
      return {
        exists: true,
        data: userData,
        // ✅ CORRIGIDO: Usar helpers centralizados importados do AuthContext
        temAssinaturaAtiva: isContaAtiva(userData),
        foiArquivada: isContaArquivada(userData),
        estaBloqueada: isContaBloqueada(userData),
        planoAtual: userData.plano || 'basic'
      };
    } catch (error) {
      console.error('Erro ao verificar estado da conta:', error);
      return { exists: false, error };
    }
  }

  // ============================================
  // ✅ CORRIGIDO: Limpar dados de arquivamento
  // Usando deleteField() ao invés de null
  // ============================================
  async function limparDadosArquivamento(uid, novosDados) {
    try {
      const docRef = doc(db, "pharmacies", uid);
      
      // ✅ Usa deleteField() para remover campos corretamente
      await updateDoc(docRef, {
        ...novosDados,
        arquivamento: deleteField(),
        cancelamento: deleteField(),
        bloqueada: deleteField(),
        atualizadoEm: new Date().toISOString()
      });
      
      console.log('✅ Dados de arquivamento limpos para UID:', uid);
      return true;
    } catch (error) {
      console.error('Erro ao limpar dados de arquivamento:', error);
      return false;
    }
  }

  // ============================================
  // ✅ Tentar login automático com email existente
  // ============================================
  async function tentarLoginAutomatico(email, password) {
    try {
      console.log('🔄 Tentando login automático para:', email);
      const loginCredential = await signInWithEmailAndPassword(auth, email, password);
      const uid = loginCredential.user.uid;
      const token = await loginCredential.user.getIdToken();
      
      console.log('✅ Login automático bem-sucedido - UID:', uid);
      
      return {
        success: true,
        uid,
        token,
        user: loginCredential.user
      };
    } catch (error) {
      console.log('❌ Login automático falhou:', error.code);
      return {
        success: false,
        code: error.code,
        message: error.message
      };
    }
  }

  // ============================================
  // ✅ SUBMIT - REGISTRO + STRIPE (CORRIGIDO)
  // ============================================
  async function handleSubmit(e) {
    e.preventDefault();
    limparMensagem();
    
    if (!validateForm()) {
      exibirMensagem('aviso', 'Por favor, corrija os campos destacados em vermelho antes de continuar.');
      return;
    }

    setIsProcessing(true);
    
    try {
      // ============================================
      // ESTRUTURA PADRONIZADA
      // ============================================
      const companyData = {
        name: formData.name.trim(),
        tradeName: formData.tradeName.trim() || formData.name.trim(),
        cnpj: formData.cnpj,
        crf: formData.crf.trim().toUpperCase(),
        phone: formData.phone,
        afe: '',
        address: {
          street: '',
          number: '',
          complement: '',
          neighborhood: '',
          city: '',
          state: '',
          zipCode: ''
        },
        plano: selectedPlan
      };
      
      // 1️⃣ TENTAR REGISTRAR CONTA
      console.log('🏪 Registrando farmácia com plano:', selectedPlan);
      const result = await register(formData.email, formData.password, companyData);
      
      // ============================================
      // ✅ Se email já existe, tenta login automático
      // ============================================
      if (!result.success && (result.code === 'auth/email-already-in-use' || result.code === 'auth/email-ja-cadastrado')) {
        console.log('📧 Email já cadastrado - tentando login automático...');
        
        const loginResult = await tentarLoginAutomatico(formData.email, formData.password);
        
        if (loginResult.success) {
          // ✅ Login funcionou - verificar estado da conta
          const estadoConta = await verificarEstadoConta(loginResult.uid);
          
          // ✅ Cenário 1: Conta arquivada - BLOQUEIA
          if (estadoConta.foiArquivada) {
            // ✅ CORRIGIDO: Fazer logout antes de exibir mensagem
            await firebaseSignOut(auth);
            exibirMensagem(
              'bloqueado',
              'Esta conta foi encerrada permanentemente. Para usar a Scriptus Pharma novamente, você precisa criar uma conta com um email diferente. Se precisar de ajuda, entre em contato com nosso suporte.'
            );
            setIsProcessing(false);
            return;
          }
          
          // ✅ Cenário 2: Conta bloqueada - BLOQUEIA
          if (estadoConta.estaBloqueada) {
            await firebaseSignOut(auth);
            exibirMensagem(
              'bloqueado',
              'Sua conta está temporariamente desativada. Entre em contato com o suporte através do email suporte@scriptusrxia.com.br para mais informações.'
            );
            setIsProcessing(false);
            return;
          }
          
          // ✅ Cenário 3: Já tem assinatura ativa - vai pro dashboard
          if (estadoConta.temAssinaturaAtiva) {
            console.log('✅ Farmácia já tem assinatura ativa - redirecionando para dashboard');
            toast.success('Bem-vindo de volta! Redirecionando para o dashboard...');
            navigate('/dashboard');
            return;
          }
          
          // ✅ Cenário 4: Conta existe mas sem assinatura - permite continuar
          console.log('ℹ️ Farmácia sem assinatura ativa - permitindo continuar');
          
          // Limpa dados de arquivamento se existirem (para reativação)
          if (estadoConta.exists) {
            await limparDadosArquivamento(loginResult.uid, {
              plano: selectedPlan,
              statusAssinatura: selectedPlan === 'basic' ? 'ativa' : 'pendente'
            });
          }
          
          // Se plano Basic, ativa direto
          if (selectedPlan === 'basic') {
            toast.success('🎉 Conta reativada com sucesso! Bem-vindo de volta ao Scriptus Pharma!');
            navigate(returnUrl || '/dashboard');
            return;
          }
          
          // Se plano Partner, continua para checkout
          if (selectedPlan === 'partner') {
            toast.loading('Redirecionando para pagamento...', { id: 'checkout-loading' });
            
            // Delay estratégico para garantir token disponível
            await new Promise(resolve => setTimeout(resolve, 500));
            
            try {
              const checkoutData = await createPartnerCheckout(loginResult.token);
              toast.dismiss('checkout-loading');
              
              if (checkoutData.url) {
                console.log('🔗 Redirecionando para Stripe');
                window.location.href = checkoutData.url;
              } else if (checkoutData.hasActiveSubscription) {
                toast.success('Você já possui uma assinatura Partner ativa!');
                navigate('/dashboard');
              } else {
                throw new Error('URL de checkout não retornada');
              }
            } catch (checkoutError) {
              toast.dismiss('checkout-loading');
              console.error('Erro no checkout:', checkoutError);
              exibirMensagem('aviso', 
                'Sua conta foi reativada! Houve um problema ao conectar com o pagamento. ' +
                'Você pode assinar o plano Partner na página de Assinatura. Redirecionando...'
              );
              setTimeout(() => navigate('/assinatura?upgrade=partner'), 3000);
            }
            return;
          }
        } else {
          // Login falhou - senha incorreta ou outro erro
          const { tipo, mensagem } = tratarErroRegistro(loginResult.code, loginResult.message);
          exibirMensagem(tipo, mensagem);
          setIsProcessing(false);
          return;
        }
      }
      
      // Se registro falhou por outro motivo
      if (!result.success) {
        const errorCode = result.code || '';
        const { tipo, mensagem } = tratarErroRegistro(errorCode, result.error);
        exibirMensagem(tipo, mensagem);
        setIsProcessing(false);
        return;
      }

      // ✅ Registro bem-sucedido
      console.log('✅ Conta criada - UID:', result.uid, 'Plano:', result.plano);

      // 2️⃣ SE PLANO BASIC → VAI DIRETO PARA DASHBOARD
      if (selectedPlan === 'basic' || !result.requerPagamento) {
        toast.success('🎉 Cadastro realizado com sucesso! Bem-vindo ao Scriptus Pharma!');
        
        if (returnUrl) {
          navigate(returnUrl);
        } else {
          navigate('/dashboard');
        }
        return;
      }

      // 3️⃣ SE PLANO PARTNER → CRIA CHECKOUT STRIPE
      if (selectedPlan === 'partner' && result.requerPagamento) {
        toast.loading('Redirecionando para pagamento...', { id: 'checkout-loading' });
        
        try {
          const token = result.token;
          
          if (!token) {
            console.error('Token de autenticação não disponível');
            throw new Error('Não foi possível obter dados de autenticação');
          }

          console.log('💳 Criando checkout para UID:', result.uid);
          
          // Delay estratégico para garantir token disponível
          await new Promise(resolve => setTimeout(resolve, 500));
          
          const checkoutData = await createPartnerCheckout(token);

          toast.dismiss('checkout-loading');

          if (checkoutData.url) {
            console.log('🔗 Redirecionando para Stripe');
            window.location.href = checkoutData.url;
          } else if (checkoutData.hasActiveSubscription) {
            toast.success('Você já possui uma assinatura Partner ativa!');
            navigate('/dashboard');
          } else if (checkoutData.reativado) {
            toast.success('Assinatura reativada com sucesso!');
            navigate('/dashboard');
          } else {
            throw new Error('URL de checkout não retornada');
          }
          
        } catch (checkoutError) {
          toast.dismiss('checkout-loading');
          console.error('Erro no checkout:', checkoutError);
          
          exibirMensagem('aviso', 
            'Sua conta foi criada com sucesso! Houve um problema ao conectar com o pagamento. ' +
            'Você pode assinar o plano Partner na página de Assinatura. ' +
            'Redirecionando...'
          );
          
          setTimeout(() => {
            navigate('/assinatura?upgrade=partner');
          }, 3000);
        }
      }

    } catch (error) {
      console.error('Erro no registro:', error);
      exibirMensagem('erro', 'Ocorreu um erro inesperado. Por favor, tente novamente.');
    } finally {
      setIsProcessing(false);
    }
  }

  // ============================================
  // LOADING STATE
  // ============================================
  const isLoading = authLoading || isProcessing;

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 relative">
      {/* Background Pattern */}
      <div className="fixed inset-0 z-0 pointer-events-none opacity-15">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            radial-gradient(circle at 20% 30%, rgb(16 185 129 / 0.08) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgb(20 184 166 / 0.06) 0%, transparent 50%)
          `
        }}></div>
      </div>

      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrollY > 10 ? 'glass-header-scrolled' : 'glass-header'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <a 
              href="https://scriptusrxia.com.br/" 
              className="group transition-all duration-300 hover:opacity-80"
            >
              <img 
                src="/logo.PNG"
                alt="Scriptus RxIA" 
                className="h-4 sm:h-5 w-auto"
              />
            </a>
            
            <Link to="/login" className="button-glass text-sm text-gray-600 hover:text-gray-800">
              Entrar
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex items-center justify-center min-h-screen pt-20 pb-8 px-4 sm:px-6 relative z-10">
        <div className="w-full max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            
            {/* PAINEL INFORMATIVO - ESQUERDA */}
            <div className="text-center lg:text-left order-2 lg:order-1 lg:mt-8">
              {/* Badge */}
              <div className="glass-badge inline-flex items-center gap-2 px-4 py-2 mb-6">
                <Sparkles className="w-4 h-4 text-emerald-600" />
                <span className="sf-pro-text font-medium text-gray-700 text-sm">
                  {selectedPlan === 'partner' ? 'Torne-se um Parceiro' : '100% Gratuito para farmácias'}
                </span>
              </div>
              
              {/* Headline */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl sf-pro-display font-medium leading-tight mb-6 tracking-tight">
                <span className="text-gray-900">Gere seu XML</span>
                <br />
                <span className="text-emerald-600">SNGPC</span>
                <span className="text-gray-600"> em segundos</span>
              </h1>
              
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-8 max-w-lg sf-pro-text font-normal">
                Valide receitas digitais ICP-Brasil e gere automaticamente o arquivo XML da ANVISA.
                {selectedPlan === 'partner' && ' Como Partner, tenha acesso ilimitado e receba leads de pacientes.'}
              </p>

              {/* Features dinâmicas baseadas no plano */}
              <div className="space-y-4 mb-8">
                {selectedPlan === 'basic' ? (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="icon-container p-2 rounded-xl bg-emerald-100/80 border-emerald-200/50">
                        <Shield className="w-4 h-4 text-emerald-600" />
                      </div>
                      <span className="text-gray-700 sf-pro-text font-medium">Validação automática ICP-Brasil</span>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="icon-container p-2 rounded-xl bg-teal-100/80 border-teal-200/50">
                        <FileText className="w-4 h-4 text-teal-600" />
                      </div>
                      <span className="text-gray-700 sf-pro-text font-medium">XML SNGPC no padrão ANVISA</span>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="icon-container p-2 rounded-xl bg-cyan-100/80 border-cyan-200/50">
                        <Zap className="w-4 h-4 text-cyan-600" />
                      </div>
                      <span className="text-gray-700 sf-pro-text font-medium">De 10 min para 30 segundos</span>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="icon-container p-2 rounded-xl bg-blue-100/80 border-blue-200/50">
                        <Clock className="w-4 h-4 text-blue-600" />
                      </div>
                      <span className="text-gray-700 sf-pro-text font-medium">50 dispensações gratuitas/mês</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="icon-container p-2 rounded-xl bg-purple-100/80 border-purple-200/50">
                        <Infinity className="w-4 h-4 text-purple-600" />
                      </div>
                      <span className="text-gray-700 sf-pro-text font-medium">Dispensações ilimitadas</span>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="icon-container p-2 rounded-xl bg-emerald-100/80 border-emerald-200/50">
                        <Users className="w-4 h-4 text-emerald-600" />
                      </div>
                      <span className="text-gray-700 sf-pro-text font-medium">Receba leads de pacientes</span>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="icon-container p-2 rounded-xl bg-teal-100/80 border-teal-200/50">
                        <TrendingUp className="w-4 h-4 text-teal-600" />
                      </div>
                      <span className="text-gray-700 sf-pro-text font-medium">Visibilidade no marketplace</span>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="icon-container p-2 rounded-xl bg-amber-100/80 border-amber-200/50">
                        <Crown className="w-4 h-4 text-amber-600" />
                      </div>
                      <span className="text-gray-700 sf-pro-text font-medium">Badge de Parceiro Oficial</span>
                    </div>
                  </>
                )}
              </div>

              {/* Stats */}
              <div className="glass-card p-5 rounded-2xl shadow-sm mb-8">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl sf-pro-display font-semibold text-emerald-600">30s</div>
                    <div className="text-xs sf-pro-text text-gray-600">Por XML</div>
                  </div>
                  <div>
                    <div className="text-2xl sf-pro-display font-semibold text-teal-600">&lt;1%</div>
                    <div className="text-xs sf-pro-text text-gray-600">Taxa de erro</div>
                  </div>
                  <div>
                    <div className="text-2xl sf-pro-display font-semibold text-cyan-600">
                      {selectedPlan === 'partner' ? '∞' : '50'}
                    </div>
                    <div className="text-xs sf-pro-text text-gray-600">
                      {selectedPlan === 'partner' ? 'Ilimitado' : 'Disp./mês'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Comparativo Resumido */}
              <div className="glass-card p-5 rounded-2xl shadow-sm hidden lg:block">
                <h3 className="sf-pro-text font-semibold text-gray-800 mb-4 text-sm">Antes vs Depois</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 sf-pro-text">Tempo por receita</span>
                    <div className="flex items-center gap-3">
                      <span className="text-red-500 line-through">10-15 min</span>
                      <ArrowRight className="w-3 h-3 text-gray-400" />
                      <span className="text-emerald-600 font-semibold">30 seg</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 sf-pro-text">Taxa de erros</span>
                    <div className="flex items-center gap-3">
                      <span className="text-red-500 line-through">~15%</span>
                      <ArrowRight className="w-3 h-3 text-gray-400" />
                      <span className="text-emerald-600 font-semibold">&lt; 1%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 sf-pro-text">
                      {selectedPlan === 'partner' ? 'Investimento' : 'Custo mensal'}
                    </span>
                    <div className="flex items-center gap-3">
                      <span className="text-red-500 line-through">R$ 200-500</span>
                      <ArrowRight className="w-3 h-3 text-gray-400" />
                      <span className={`font-semibold ${selectedPlan === 'partner' ? 'text-purple-600' : 'text-emerald-600'}`}>
                        {selectedPlan === 'partner' ? 'R$ 100' : 'R$ 0'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Como Funciona */}
              <div className="glass-card p-5 rounded-2xl shadow-sm mt-6 hidden lg:block">
                <h3 className="sf-pro-text font-semibold text-gray-800 mb-4 text-sm">Como Funciona</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center text-white text-xs font-bold sf-pro-display flex-shrink-0">
                      1
                    </div>
                    <div>
                      <p className="sf-pro-text font-medium text-gray-800 text-sm">Escaneie o QR Code</p>
                      <p className="text-xs sf-pro-text text-gray-600">A receita é validada automaticamente</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center text-white text-xs font-bold sf-pro-display flex-shrink-0">
                      2
                    </div>
                    <div>
                      <p className="sf-pro-text font-medium text-gray-800 text-sm">Preencha lote e validade</p>
                      <p className="text-xs sf-pro-text text-gray-600">Complete os dados do medicamento</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center text-white text-xs font-bold sf-pro-display flex-shrink-0">
                      3
                    </div>
                    <div>
                      <p className="sf-pro-text font-medium text-gray-800 text-sm">Baixe o XML pronto</p>
                      <p className="text-xs sf-pro-text text-gray-600">Envie diretamente à ANVISA</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* FORMULÁRIO - DIREITA */}
            <div className="relative order-1 lg:order-2">
              <div className="glass-card-elevated rounded-2xl p-6 sm:p-8 shadow-xl">
                {/* Header do Form */}
                <div className="text-center mb-6">
                  <div className={`icon-container-pharma w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg ${
                    selectedPlan === 'partner' ? 'bg-gradient-to-br from-purple-100 to-purple-50' : ''
                  }`}>
                    {selectedPlan === 'partner' ? (
                      <Crown className="w-7 h-7 text-purple-600" />
                    ) : (
                      <QrCode className="w-7 h-7 text-emerald-600" />
                    )}
                  </div>
                  <h2 className="text-2xl sf-pro-display font-semibold text-gray-900 mb-2 tracking-tight">
                    Cadastre sua <span className={selectedPlan === 'partner' ? 'text-purple-600' : 'text-emerald-600'}>Farmácia</span>
                  </h2>
                  <p className="text-gray-600 sf-pro-text font-medium text-sm">
                    {selectedPlan === 'partner' 
                      ? 'Torne-se um Parceiro Oficial Scriptus'
                      : 'Comece a gerar XMLs SNGPC gratuitamente'
                    }
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Sistema de mensagens */}
                  {erro && mensagemTipo && (
                    <div className={`glass-alert ${getEstiloMensagem(mensagemTipo)} rounded-xl border`}>
                      <div className="flex items-start gap-3">
                        <IconeMensagem tipo={mensagemTipo} />
                        <div className="flex-1 min-w-0">
                          <p className="sf-pro-text text-sm leading-relaxed">{erro}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Dados da Empresa */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm sf-pro-text font-semibold text-gray-700">
                      <Building2 className="w-4 h-4 text-emerald-500" />
                      Dados da Farmácia
                    </div>
                    
                    {/* Razão Social */}
                    <div>
                      <label className="block text-sm sf-pro-text font-medium text-gray-700 mb-1.5">Razão Social *</label>
                      <input
                        name="name"
                        placeholder="Drogaria Saúde Total Ltda"
                        value={formData.name}
                        onChange={handleChange}
                        disabled={isLoading}
                        className={`input-glass w-full ${errors.name ? 'border-red-400 focus:border-red-400' : ''}`}
                      />
                      {errors.name && <p className="text-red-500 text-xs mt-1 sf-pro-text">{errors.name}</p>}
                    </div>
                    
                    {/* Nome Fantasia */}
                    <div>
                      <label className="block text-sm sf-pro-text font-medium text-gray-700 mb-1.5">Nome Fantasia</label>
                      <input
                        name="tradeName"
                        placeholder="Drogaria Saúde Total"
                        value={formData.tradeName}
                        onChange={handleChange}
                        disabled={isLoading}
                        className="input-glass w-full"
                      />
                      <p className="text-xs text-gray-500 mt-1 sf-pro-text">Opcional - usa Razão Social se não preenchido</p>
                    </div>
                    
                    {/* CNPJ e CRF */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm sf-pro-text font-medium text-gray-700 mb-1.5">CNPJ *</label>
                        <input
                          name="cnpj"
                          placeholder="00.000.000/0000-00"
                          value={formData.cnpj}
                          onChange={handleChange}
                          disabled={isLoading}
                          className={`input-glass w-full ${errors.cnpj ? 'border-red-400 focus:border-red-400' : ''}`}
                        />
                        {errors.cnpj && <p className="text-red-500 text-xs mt-1 sf-pro-text">{errors.cnpj}</p>}
                      </div>
                      
                      <div>
                        <label className="block text-sm sf-pro-text font-medium text-gray-700 mb-1.5">CRF *</label>
                        <div className="relative">
                          <FileCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            name="crf"
                            placeholder="12345-SP"
                            value={formData.crf}
                            onChange={handleChange}
                            disabled={isLoading}
                            className={`input-glass w-full pl-10 ${errors.crf ? 'border-red-400 focus:border-red-400' : ''}`}
                          />
                        </div>
                        {errors.crf && <p className="text-red-500 text-xs mt-1 sf-pro-text">{errors.crf}</p>}
                      </div>
                    </div>
                    
                    {/* Telefone */}
                    <div>
                      <label className="block text-sm sf-pro-text font-medium text-gray-700 mb-1.5">Telefone</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          name="phone"
                          placeholder="(11) 99999-9999"
                          value={formData.phone}
                          onChange={handleChange}
                          disabled={isLoading}
                          className="input-glass w-full pl-10"
                        />
                      </div>
                    </div>

                    {/* Info sobre dados adicionais */}
                    <div className="glass-alert bg-blue-50/80 border-blue-200/50 rounded-xl border p-3">
                      <div className="flex items-start gap-2">
                        <Shield className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                        <p className="sf-pro-text text-xs text-blue-700 leading-relaxed">
                          <strong>AFE e Endereço</strong> podem ser preenchidos depois nas Configurações para completar seu cadastro SNGPC.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Dados de Acesso */}
                  <div className="space-y-4 pt-4 border-t border-gray-200/50">
                    <div className="flex items-center gap-2 text-sm sf-pro-text font-semibold text-gray-700">
                      <Lock className="w-4 h-4 text-emerald-500" />
                      Dados de Acesso
                    </div>
                    
                    {/* Email */}
                    <div>
                      <label className="block text-sm sf-pro-text font-medium text-gray-700 mb-1.5">Email *</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          name="email"
                          type="email"
                          placeholder="farmacia@email.com"
                          value={formData.email}
                          onChange={handleChange}
                          disabled={isLoading}
                          className={`input-glass w-full pl-10 ${errors.email ? 'border-red-400 focus:border-red-400' : ''}`}
                        />
                      </div>
                      {errors.email && <p className="text-red-500 text-xs mt-1 sf-pro-text">{errors.email}</p>}
                    </div>
                    
                    {/* Senhas */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm sf-pro-text font-medium text-gray-700 mb-1.5">Senha *</label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            name="password"
                            type="password"
                            placeholder="••••••"
                            value={formData.password}
                            onChange={handleChange}
                            disabled={isLoading}
                            className={`input-glass w-full pl-10 ${errors.password ? 'border-red-400 focus:border-red-400' : ''}`}
                          />
                        </div>
                        {errors.password && <p className="text-red-500 text-xs mt-1 sf-pro-text">{errors.password}</p>}
                      </div>
                      
                      <div>
                        <label className="block text-sm sf-pro-text font-medium text-gray-700 mb-1.5">Confirmar *</label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            name="confirmPassword"
                            type="password"
                            placeholder="••••••"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            disabled={isLoading}
                            className={`input-glass w-full pl-10 ${errors.confirmPassword ? 'border-red-400 focus:border-red-400' : ''}`}
                          />
                        </div>
                        {errors.confirmPassword && <p className="text-red-500 text-xs mt-1 sf-pro-text">{errors.confirmPassword}</p>}
                      </div>
                    </div>
                  </div>
                  
                  {/* SELEÇÃO DE PLANO */}
                  <div className="space-y-3 pt-4 border-t border-gray-200/50">
                    <div className="text-sm sf-pro-text font-semibold text-gray-700">Escolha seu Plano</div>
                    
                    {/* Plano Basic */}
                    <button
                      type="button"
                      onClick={() => handlePlanSelect('basic')}
                      disabled={isLoading}
                      className={`w-full text-left p-4 rounded-xl transition-all duration-200 ${
                        selectedPlan === 'basic' 
                          ? 'glass-plan-selected' 
                          : 'glass-plan hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                            selectedPlan === 'basic' 
                              ? 'bg-emerald-500' 
                              : 'border-2 border-gray-300'
                          }`}>
                            {selectedPlan === 'basic' && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="sf-pro-text font-semibold text-gray-800">Basic</span>
                              <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full sf-pro-text font-semibold">
                                GRÁTIS
                              </span>
                            </div>
                            <p className="text-gray-600 text-xs mt-0.5 sf-pro-text">
                              Validação ICP-Brasil + XML SNGPC + 50 dispensações/mês
                            </p>
                          </div>
                        </div>
                        <span className="sf-pro-display font-bold text-emerald-600 text-lg">R$ 0</span>
                      </div>
                    </button>
                    
                    {/* Plano Partner */}
                    <button
                      type="button"
                      onClick={() => handlePlanSelect('partner')}
                      disabled={isLoading}
                      className={`w-full text-left p-4 rounded-xl transition-all duration-200 relative overflow-hidden ${
                        selectedPlan === 'partner' 
                          ? 'glass-plan-selected-partner' 
                          : 'glass-plan-partner hover:border-purple-300'
                      }`}
                    >
                      {/* Indicador de Recomendado */}
                      <div className="absolute top-0 right-0">
                        <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg sf-pro-text">
                          RECOMENDADO
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                            selectedPlan === 'partner' 
                              ? 'bg-purple-500' 
                              : 'border-2 border-purple-300'
                          }`}>
                            {selectedPlan === 'partner' && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="sf-pro-text font-semibold text-gray-800">Partner</span>
                              <Crown className="w-4 h-4 text-purple-500" />
                            </div>
                            <p className="text-gray-600 text-xs mt-0.5 sf-pro-text">
                              Ilimitado + Marketplace + Leads de pacientes
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="sf-pro-display font-bold text-purple-600 text-lg">R$ 100</span>
                          <span className="text-gray-500 text-xs sf-pro-text">/mês</span>
                        </div>
                      </div>

                      {/* Lista de benefícios extras quando selecionado */}
                      {selectedPlan === 'partner' && (
                        <div className="mt-3 pt-3 border-t border-purple-200/50 grid grid-cols-2 gap-2">
                          <div className="flex items-center gap-1.5 text-xs text-purple-700">
                            <Check className="w-3 h-3" />
                            <span>Dispensações ∞</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-purple-700">
                            <Check className="w-3 h-3" />
                            <span>Leads de pacientes</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-purple-700">
                            <Check className="w-3 h-3" />
                            <span>Marketplace</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-purple-700">
                            <Check className="w-3 h-3" />
                            <span>Analytics avançado</span>
                          </div>
                        </div>
                      )}
                    </button>
                  </div>
                  
                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`button-primary w-full ${
                      selectedPlan === 'partner'
                        ? 'bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700'
                        : 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600'
                    } text-white`}
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span className="sf-pro-text font-semibold">
                          {selectedPlan === 'partner' ? 'Processando...' : 'Cadastrando...'}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <span className="sf-pro-text font-semibold">
                          {selectedPlan === 'partner' 
                            ? 'Assinar Partner - R$ 100/mês' 
                            : 'Criar Conta Grátis'
                          }
                        </span>
                        <ArrowRight className="w-5 h-5" />
                      </div>
                    )}
                  </button>

                  {/* Info de pagamento para Partner */}
                  {selectedPlan === 'partner' && (
                    <div className="flex items-center justify-center gap-2 text-xs text-gray-500 sf-pro-text">
                      <Lock className="w-3 h-3" />
                      <span>Pagamento seguro via Stripe • Cancele quando quiser</span>
                    </div>
                  )}
                </form>
                
                {/* Link para login */}
                <p className="text-center text-gray-600 mt-5 text-sm sf-pro-text">
                  Já tem uma conta?{' '}
                  <Link to="/login" className="text-emerald-600 hover:text-emerald-700 sf-pro-text font-semibold hover:underline transition">
                    Fazer login
                  </Link>
                </p>
              </div>

              {/* Elementos decorativos */}
              <div className={`absolute -top-4 -right-4 w-24 h-24 rounded-full opacity-10 blur-xl ${
                selectedPlan === 'partner' 
                  ? 'bg-gradient-to-br from-purple-400 to-purple-600' 
                  : 'bg-gradient-to-br from-emerald-400 to-teal-500'
              }`}></div>
              <div className={`absolute -bottom-4 -left-4 w-32 h-32 rounded-full opacity-10 blur-xl ${
                selectedPlan === 'partner'
                  ? 'bg-gradient-to-br from-purple-300 to-pink-400'
                  : 'bg-gradient-to-br from-teal-400 to-cyan-500'
              }`}></div>
            </div>
          </div>
        </div>
      </div>

      {/* SEÇÃO SEGURANÇA */}
      <section className="py-12 px-6 bg-white/50 backdrop-blur-sm border-t border-gray-200/50 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sf-pro-display font-semibold text-gray-900 mb-3 tracking-tight">
              Compliance e <span className="text-emerald-600">Segurança</span>
            </h2>
            <p className="text-gray-600 sf-pro-text font-medium">Tecnologias de ponta para proteger sua farmácia</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="glass-card rounded-2xl p-6 text-center shadow-sm">
              <div className="icon-container w-12 h-12 bg-emerald-100/80 border-emerald-200/50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-emerald-600" />
              </div>
              <h3 className="sf-pro-text font-semibold text-gray-800 mb-2">Validação ICP-Brasil</h3>
              <p className="text-sm sf-pro-text text-gray-600">Receitas verificadas com certificado digital</p>
            </div>

            <div className="glass-card rounded-2xl p-6 text-center shadow-sm">
              <div className="icon-container w-12 h-12 bg-teal-100/80 border-teal-200/50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <FileText className="w-6 h-6 text-teal-600" />
              </div>
              <h3 className="sf-pro-text font-semibold text-gray-800 mb-2">SNGPC Automático</h3>
              <p className="text-sm sf-pro-text text-gray-600">XML gerado conforme padrão ANVISA</p>
            </div>

            <div className="glass-card rounded-2xl p-6 text-center shadow-sm">
              <div className="icon-container w-12 h-12 bg-blue-100/80 border-blue-200/50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Lock className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="sf-pro-text font-semibold text-gray-800 mb-2">LGPD Compliant</h3>
              <p className="text-sm sf-pro-text text-gray-600">Dados protegidos e criptografados</p>
            </div>
          </div>
        </div>
      </section>

      {/* STYLES */}
      <style>{`
        .sf-pro-display {
          font-family: -apple-system, "SF Pro Display", BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
          font-feature-settings: "liga" 1, "kern" 1;
          -webkit-font-smoothing: antialiased;
        }
        
        .sf-pro-text {
          font-family: -apple-system, "SF Pro Text", BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
          font-feature-settings: "liga" 1, "kern" 1;
          -webkit-font-smoothing: antialiased;
        }

        .glass-card {
          background: rgba(255, 255, 255, 0.75);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
        }

        .glass-card-elevated {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        }

        .icon-container {
          background: rgba(255, 255, 255, 0.6);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
        }

        .icon-container-pharma {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          box-shadow: 0 4px 16px rgba(16, 185, 129, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8);
        }

        .glass-header {
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
        }

        .glass-header-scrolled {
          background: rgba(255, 255, 255, 0.75);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.3);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }

        .input-glass {
          padding: 12px 14px;
          background: rgba(255, 255, 255, 0.7);
          border: 1px solid rgba(209, 213, 219, 0.5);
          border-radius: 10px;
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          transition: all 0.2s ease;
          font-family: -apple-system, "SF Pro Text", BlinkMacSystemFont, sans-serif;
          font-size: 14px;
          color: #1f2937;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
        }

        .input-glass::placeholder { color: #9ca3af; }

        .input-glass:focus {
          outline: none;
          background: rgba(255, 255, 255, 0.9);
          border-color: rgba(16, 185, 129, 0.5);
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1), 0 4px 12px rgba(0, 0, 0, 0.06);
        }

        .input-glass:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .button-glass {
          padding: 8px 16px;
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 10px;
          font-family: -apple-system, "SF Pro Text", BlinkMacSystemFont, sans-serif;
          font-weight: 500;
          transition: all 0.2s ease;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
        }

        .button-glass:hover {
          background: rgba(255, 255, 255, 0.9);
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
        }

        .button-primary {
          padding: 14px 24px;
          border-radius: 12px;
          font-weight: 600;
          transition: all 0.2s ease;
          font-size: 16px;
          box-shadow: 0 4px 14px rgba(16, 185, 129, 0.3);
        }

        .button-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
        }

        .button-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .glass-badge {
          background: rgba(236, 253, 245, 0.9);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          color: #059669;
          border-radius: 9999px;
          border: 1px solid rgba(16, 185, 129, 0.2);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
        }

        .glass-alert {
          padding: 14px 16px;
          border-radius: 12px;
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        }

        .glass-plan {
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          border: 2px solid rgba(209, 213, 219, 0.4);
          transition: all 0.2s ease;
        }

        .glass-plan:hover:not(:disabled) {
          border-color: rgba(156, 163, 175, 0.6);
        }

        .glass-plan-selected {
          background: linear-gradient(135deg, rgba(236, 253, 245, 0.9) 0%, rgba(209, 250, 229, 0.8) 100%);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 2px solid rgba(16, 185, 129, 0.4);
          box-shadow: 0 4px 16px rgba(16, 185, 129, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5);
        }

        .glass-plan-partner {
          background: rgba(250, 245, 255, 0.5);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          border: 2px solid rgba(168, 85, 247, 0.3);
          transition: all 0.2s ease;
        }

        .glass-plan-partner:hover:not(:disabled) {
          border-color: rgba(168, 85, 247, 0.5);
        }

        .glass-plan-selected-partner {
          background: linear-gradient(135deg, rgba(250, 245, 255, 0.95) 0%, rgba(243, 232, 255, 0.9) 100%);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 2px solid rgba(168, 85, 247, 0.5);
          box-shadow: 0 4px 16px rgba(168, 85, 247, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.5);
        }

        html { scroll-behavior: smooth; }

        button:focus-visible, input:focus-visible {
          outline: 2px solid #10b981;
          outline-offset: 2px;
        }

        @media (max-width: 768px) {
          .glass-card-elevated, .glass-card {
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
          }
          .input-glass { font-size: 16px; }
          .button-primary { padding: 14px 20px; }
        }

        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            transition-duration: 0.01ms !important;
          }
        }

        @media (prefers-contrast: high) {
          .glass-card-elevated, .glass-card {
            background: rgba(255, 255, 255, 0.95);
            border: 2px solid rgba(0, 0, 0, 0.2);
          }
          .input-glass {
            background: rgba(255, 255, 255, 0.95);
            border: 2px solid rgba(0, 0, 0, 0.3);
          }
        }

        .glass-card, .glass-card-elevated, .button-glass, .button-primary, .input-glass {
          transform: translateZ(0);
          backface-visibility: hidden;
        }
      `}</style>
    </div>
  );
}